import React, { Fragment, useState, useEffect } from "react";
import {
  CalendarIcon,
  CheckIcon,
  CogIcon,
  DownloadIcon,
  SearchIcon,
  XCircleIcon,
} from "@heroicons/react/solid";
import {
  Form,
  Button,
  InputGroup,
  Dropdown,
  Nav,
  Card,
  Table,
  Pagination,
  Tab,
} from "react-bootstrap";
import {
  useDnldRCSCampaignSummaryMutation,
  useDnldRCScdrReportsMutation,
  useDnldRCSErrorCodeSummaryMutation,
  useDnldScheduledCampMutation,
  useRCSCampaignSummaryMutation,
  useRCSCdrReportsMutation,
  useRcsErrorCodeSummaryMutation,
  useScheduledCampMutation,
} from "Services/services";
import moment from "moment-timezone";
import Datetime from "react-datetime";
import hasPermission from "pages/Permissions";
import { downloadBlobFile } from "components/Utility";
import Select from "react-select";

export default () => {
  // Get the user type from localStorage :: UI
  const userData = localStorage.getItem("TOKEN");
  const userObject = JSON.parse(userData);
  const userType = userObject.user_type;
  const userID = userObject.id;

  // affects Tab UI only after routing from another component
  const [tabType, setTabType] = useState("tab1");

  const [tableData, setTableData] = useState([]);

  const [searchValue, setSearchValue] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [status, setStatus] = useState("");

  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  const [pagelimit, setPagelimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState("");
  const [totalPosts, setTotalPosts] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");

  const [CampaignList] = useRCSCampaignSummaryMutation();
  const [dnldCamp] = useDnldRCSCampaignSummaryMutation();
  const [errorCodes] = useRcsErrorCodeSummaryMutation();
  const [dnldErrorCodes] = useDnldRCSErrorCodeSummaryMutation();
  const [scheduled] = useScheduledCampMutation();
  const [dnldScheduled] = useDnldScheduledCampMutation();
  const [rcsCdrReport] = useRCSCdrReportsMutation();
  const [dnldCDRReport] = useDnldRCScdrReportsMutation();

  const getStatusOptions = (tabType) => {
    switch (tabType) {
      // case ("tab1"):
      //   return [
      //     { value: "delivered", label: "Delivered" },
      //     { value: "failed", label: "Failed" },
      //     { value: "read", label: "Read" }
      //   ];
      case "tab3":
        return [
          { value: "pending", label: "Pending" },
          { value: "paused", label: "Paused" },
          { value: "cancelled", label: "Cancelled" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
          { value: "inprocess", label: "In-Process" },
          { value: "running", label: "Running" },
          { value: "completed", label: "Completed" },
        ];
      default:
        return [];
    }
  };

  useEffect(() => {
    setPagelimit(10);
    setCurrentPage(1);
    setSearchValue('');
    setDebouncedValue('');
    setStart(null);
    setEnd(null);
    setStatusOptions(getStatusOptions(tabType));
    setTotalPages(0);
    setTotalPosts(0);
  }, [tabType]);

  // Debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue); // Set debounced value after 3 seconds
    }, 2000);
    // Cleanup the timeout if searchValue changes (to avoid multiple timeouts)
    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const fetchCampaign = async () => {
    try {
      const response = await CampaignList({
        user_id:
          userType === "super_admin" || userType === "admin" ? "" : userID,
        page: currentPage,
        limit: pagelimit,
        status: status,
        search: searchValue,
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      });

      if (response?.data?.statusCode === 200) {
        setTableData(response?.data?.data?.items || []);
        setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
        setTotalPosts(response?.data?.data?.totalItems || 0);
      } else {
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      }
    } catch (error) {
      alert(error.message);
      setTableData([]);
      setTotalPages(0);
      setTotalPosts(0);
    }
  };
  const fetchErrorCodes = async () => {
    try {
      const response = await errorCodes({
        code: status,
        page: currentPage,
        limit: pagelimit,
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
        search: searchValue
      });

      if (response?.data?.statusCode === 200) {
        setTableData(response?.data?.data?.items || []);
        setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
        setTotalPosts(response?.data?.data?.totalItems || 0);
      } else {
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      }
    } catch (error) {
      alert(error.message);
      setTableData([]);
      setTotalPages(0);
      setTotalPosts(0);
    }
  };
  const fetchScheduled = async () => {
    try {
      const response = await scheduled({
        page: currentPage,
        limit: pagelimit,
        campaignType: "RCS",
        search: searchValue,
        status: status,
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      });

      if (response?.data?.statusCode === 200) {
        setTableData(response?.data?.data?.items || []);
        setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
        setTotalPosts(response?.data?.data?.totalItems || 0);
      } else {
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      }
    } catch (error) {
      alert(error.message);
      setTableData([]);
      setTotalPages(0);
      setTotalPosts(0);
    }
  };
  const fetchCDR = async () => {
    try {
      const response = await rcsCdrReport({
        page: currentPage,
        limit: pagelimit,
        search: searchValue,
        status: status,
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      });
      if (response?.data?.statusCode === 200) {
        setTableData(response?.data?.data?.items || []);
        setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
        setTotalPosts(response?.data?.data?.totalItems || 0);
      } else {
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      }
    } catch (error) {
      alert(error.message);
      setTableData([]);
      setTotalPages(0);
      setTotalPosts(0);
    }
  };

  useEffect(() => {
    if (tabType === "tab1") {
      fetchCampaign();
    }
    else if (tabType === "tab2") {
      fetchErrorCodes();
    } else if (tabType === "tab3") {
      fetchScheduled();
    } else if (tabType === "tab4") {
      fetchCDR();
    } 
    // eslint-disable-next-line
  }, [
    currentPage,
    pagelimit,
    debouncedValue,
    start,
    end,
    status,
    tabType,
  ]);

  const changeSearchValue = (e) => {
    setSearchValue(e.target.value);
  };

  const dnldCampaign = async () => {
    try {
      const blob = await dnldCamp({
        campaignType: "RCS",
        search: searchValue,
        loggedin_user_id: userID,
        user_type_id: userObject.user_type_id || "1",
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      }).unwrap();
      downloadBlobFile(blob, "RCS_Campaign_Summaries.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };
  const dnldError = async () => {
    try {
      const blob = await dnldErrorCodes({
        code: status,
        search: searchValue,
        loggedin_user_id: userID,
        user_type_id: userObject.user_type_id || "1",
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      }).unwrap();
      downloadBlobFile(blob, "RCS_Error_Report.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };
  const dnldScheduledCamps = async () => {
    try {
      const blob = await dnldScheduled({
        campaignType: "RCS",
        search: searchValue,
        loggedin_user_id: userID,
        user_type_id: userObject.user_type_id || "1",
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      }).unwrap();
      downloadBlobFile(blob, "RCS_Scheduled_Campaigns.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };
  const dnldCDR = async () => {
    try {
      const blob = await dnldCDRReport({
        search: searchValue,
        loggedin_user_id: userID,
        user_type_id: userObject.user_type_id || "1",
        startDate: start ? start.format("YYYY-MM-DD") : "",
        endDate: end ? end.format("YYYY-MM-DD") : "",
      }).unwrap();
      downloadBlobFile(blob, "RCS_CDRs.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };

  // const downloadList = (ids) => { };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const canDownload = hasPermission("RCS Reports", "download");
  const handleDownload = () => {
    if (tabType === "tab1") {
      dnldCampaign();
    }
    else if (tabType === "tab2") {
      dnldError();
    } else if (tabType === "tab3") {
      dnldScheduledCamps();
    } else if (tabType === "tab4") {
      dnldCDR();
    } else {
      return;
    }
  }

  const TableRow = (props) => {
    switch (tabType) {
      case "tab1": {
        return (
          <tr className="border-bottom">
            <td><span className="fw-normal" title={props.campaign_name || "-"}>{props.campaign_name}</span></td>
            <td><span className="fw-normal" title={props.totalSubmitted || "-"}>{props.totalSubmitted}</span></td>
            <td><span className="fw-normal" title={props.totalSuccess || "-"}>{props.totalSuccess}</span></td>
            <td><span className="fw-normal" title={props.totalFailure || "-"}>{props.totalFailure}</span></td>
            <td><span className="fw-normal" title={moment(props.time).format('DD/MM/YYYY') || '-'}>{moment(props.time).format('DD/MM/YYYY') || '-'}</span></td>
            <td><span className="fw-normal" title={props.totalDeductAmount || "-"}>{props.totalDeductAmount}</span></td>
          </tr>
        );
      }

      case "tab2":
        {
          return (
            <tr className="border-bottom">
              <td><span className="fw-normal " title={props.code || '-'}>{props.code || '-'}</span></td>
              <td><span className="fw-normal">{props.totalCount || '-'}</span></td>
              <td><span className="fw-normal">{moment(props.createdAt).format('DD/MM/YYYY') || '-'}</span></td>
              <td><span className="fw-normal">{props.totalDeductAmount || '-'}</span></td>
            </tr>
          );
        }

      case "tab3":
        {
          return (
            <tr className="border-bottom">
              <td><span className="fw-normal" title={props.campaign_id || '-'}>{props.campaign_id || '-'}</span></td>
              <td><span className="fw-normal">{moment(props.scheduledTime).format('DD/MM/YYYY') || '-'}</span></td>
              <td><span className="fw-normal" title={props.actualSentTime || '-'}>{props.actualSentTime || '-'}</span></td>
              <td><span className="fw-normal" title={props.scheduled_type || '-'}>{props.scheduled_type || '-'}</span></td>
              <td><span className="fw-normal" title={props.status || '-'}>{props.status || '-'}</span></td>
            </tr>
          );
        }

        case "tab4":
          {
            return (
              <tr className="border-bottom">
                <td><span className="fw-normal" title={props.campaign_name || '-'}>{props.campaign_name || '-'}</span></td>
                <td><span className="fw-normal" title={props.phone_number || '-'}>{props.phone_number || '-'}</span></td>
                <td><span className="fw-normal" title={props.status || '-'}>{props.status || '-'}</span></td>
                <td><span className="fw-normal" title={props.template_type || '-'}>{props.template_type || '-'}</span></td>
                <td><span className="fw-normal">{moment(props.createdAt).format('DD/MM/YYYY') || '-'}</span></td>
                <td><span className="fw-normal">{moment(props.sentAt).format('DD/MM/YYYY') || '-'}</span></td>
              </tr>
            );
          }

      default: {
        return null;
      }
    }
  };

  return (
    <Fragment>

      <Card className="p-2">
        <Tab.Container
          defaultActiveKey={tabType}
          onSelect={(key) => {
            setTabType(key);
          }}
        >
          <Nav fill variant="pills" className="flex-xl-nowrap gap-2">
            <Nav.Item>
              <Nav.Link eventKey="tab1">Campaign Overview</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="tab4">CDR Report</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="tab2">Error Report</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="tab3">Scheduled Campaign Log</Nav.Link>
            </Nav.Item>
          </Nav>
        </Tab.Container>
      </Card>


      <Card border={1}>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">

            <InputGroup className="d-flex flex-nowrap fmxw-400">
              <InputGroup.Text>
                <SearchIcon className="icon icon-xs" />
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder={
                  tabType === "tab1" ? "Search Campaign-Name" :
                      tabType === "tab2" ? "Search Error Code" :
                          tabType === "tab3" ? "Search Campaign ID" : 
                          tabType === "tab4" ? "Search Campaign Name / Contact" : "Search"

                }
                value={searchValue}
                onChange={changeSearchValue}
              />
            </InputGroup>

            <Form.Group id="DateRange">
                  <InputGroup className="d-flex flex-nowrap">
                    <InputGroup.Text
                      onClick={() => {
                        setStart(null);
                        setEnd(null);
                      }}
                    >
                      {start || end ? (
                        <XCircleIcon className="icon icon-xs" />
                      ) : (
                        <CalendarIcon className="icon icon-xs" />
                      )}
                    </InputGroup.Text>
                    <Datetime
                      timeFormat={false}
                      onChange={(date) => {
                        if (moment.isMoment(date)) {
                          setStart(date);
                        }
                      }}
                      closeOnSelect={true}
                      renderInput={(props, openCalendar) => (
                        <Form.Control
                          type="search"
                          placeholder="Start Date"
                          value={
                            start ? moment(start).format("DD/MM/YYYY") : ""
                          }
                          onFocus={openCalendar}
                          readOnly
                          style={{ width: "150px", borderRadius: "0" }}
                        />
                      )}
                    />
                    <Datetime
                      timeFormat={false}
                      onChange={(date) => {
                        if (moment.isMoment(date)) {
                          setEnd(date);
                        }
                      }}
                      closeOnSelect={true}
                      isValidDate={(currDate) => currDate.isAfter(start)}
                      initialViewDate={end}
                      renderInput={(props, openCalendar) => (
                        <Form.Control
                          type="search"
                          placeholder="End Date"
                          value={end ? moment(end).format("DD/MM/YYYY") : ""}
                          onFocus={openCalendar}
                          readOnly
                          style={{
                            width: "150px",
                            borderTopLeftRadius: "0",
                            borderBottomLeftRadius: "0",
                          }}
                        />
                      )}
                    />
                  </InputGroup>
                </Form.Group>

           {tabType==='tab3' && <div style={{ minWidth: 200 }} className="fix-200">
              <Select
                options={statusOptions}
                value={statusOptions.find(option => option.value === status) || null}
                onChange={option => setStatus(option ? option.value : "")}
                isClearable
                placeholder="Select Status"
              />
            </div>}

          <div className="ms-auto d-inline-flex gap-2">
            {canDownload && (
              <Button
                variant="outline-gray-300"
                className="p-2"
                onClick={handleDownload}

              >
                <DownloadIcon className="icon icon-sm" />
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle
                split
                as={Button}
                variant="outline-gray-300"
                className="text-dark m-0 p-2"
              >
                <CogIcon className="icon icon-sm" />
                <span className="visually-hidden">
                  Toggle Dropdown
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-xs dropdown-menu-end p-2">
                <span className="small ps-2 fw-bold text-dark">
                Rows per page
                </span>
                {[10, 20, 30].map((limit) => (
                  <Dropdown.Item
                    key={limit}
                    className={`d-flex align-items-center fw-bold ${limit === 30 ? "rounded-bottom" : ""
                      }`}
                    onClick={() => {
                      setPagelimit(limit); // Update the page limit
                      setCurrentPage(1); // Reset the page number to 1
                    }}
                  >
                    {limit}{" "}
                    {pagelimit === limit && (
                      <CheckIcon className="icon icon-xxs ms-auto" />
                    )}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="custom-scroll-wrapper">
          <div className="scroll-container">
            <Table responsive className="align-items-center table-flush table-outlines">
              <thead className="thead-light">
                {tabType === 'tab1' ?
                  <tr>
                    <th className="border-bottom">Campaign Name</th>
                    <th className="border-bottom">Total Sent</th>
                    <th className="border-bottom">Delivered</th>
                    <th className="border-bottom">Failed</th>
                    <th className="border-bottom">Date</th>
                    <th className="border-bottom">Cost</th>
                  </tr>
                  : tabType === 'tab2' ?
                    <tr>
                      <th className="border-bottom">Code</th>
                      <th className="border-bottom">Total Count</th>
                      <th className="border-bottom">Created On</th>
                      <th className="border-bottom">Deducted Amount</th>
                    </tr>
                    : tabType === 'tab3' ?
                      <tr>
                        <th className="border-bottom">Campaign ID</th>
                        <th className="border-bottom">Scheduled Date</th>
                        <th className="border-bottom">Scheduled Time</th>
                        <th className="border-bottom">Scheduled Type</th>
                        <th className="border-bottom">Status</th>
                      </tr> : tabType === 'tab4' ?
                      <tr>
                        <th className="border-bottom">Campaign Name</th>
                        <th className="border-bottom">Contact</th>
                        <th className="border-bottom">Status</th>
                        <th className="border-bottom">Template Type</th>
                        <th className="border-bottom">Created On</th>
                        <th className="border-bottom">Sent On</th>
                      </tr> : ""}
              </thead>
              <tbody className="border-0">
                {tableData.length > 0 ? (
                  tableData.map((u, index) => (
                    <TableRow key={index + 1} index={index + 1} {...u} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-danger">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <Card.Footer className="px-3 border-0 d-flex flex-wrap align-items-center justify-content-between">
          <Nav>
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                Previous
              </Pagination.Prev>
              {currentPage >= 3 && (
                <>
                  <Pagination.Item onClick={() => handlePageChange(1)}>
                    1
                  </Pagination.Item>
                  <Pagination.Ellipsis />
                </>
              )}
              {currentPage > 1 && (
                <Pagination.Item
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {currentPage - 1}
                </Pagination.Item>
              )}
              <Pagination.Item active>{currentPage}</Pagination.Item>
              {currentPage < totalPages && (
                <Pagination.Item
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {currentPage + 1}
                </Pagination.Item>
              )}
              {currentPage < totalPages - 1 && (
                <>
                  <Pagination.Ellipsis />
                  <Pagination.Item
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </Pagination.Item>
                </>
              )}
              <Pagination.Next
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          </Nav>
          <small className="fw-normal small mt-2 mt-md-0">
            Showing <b>{totalPosts === 0 ? totalPosts : pagelimit * (currentPage - 1) + 1}-{(pagelimit * currentPage) > totalPosts ? totalPosts : (pagelimit * currentPage)}</b> of <b>{totalPosts}</b>{" "}entries.
          </small>
        </Card.Footer>
      </Card>
    </Fragment>
  );
};
